import React from "react";
import { useTranslation } from "react-i18next";

import styles from "./feedSearchHeader.module.css";

/**
 * The My Feed page's title while the header search is applied (`/?q=…`, GROWTH-7577): "Results for “q”" (the page's
 * only <h1>, in place of the greeting), a "Clear search" chip, and "N matches across your communities" or, with a
 * sidebar community selected, "N matches in {community}". The count is the API's exact `total`; without one the line
 * is left out. The chip's accessible name, "Clear search results", tells it apart from the header input's own "Clear search"
 * button, which only empties the box; it starts with the visible words (WCAG 2.5.3).
 *
 * @param {Object} props
 * @param {string} props.query the text searched
 * @param {number|null} [props.total] GET /api-v2/feed's `total`; null/undefined = not known yet
 * @param {string|null} [props.communityName] the selected sidebar community, if any
 * @param {Function} props.onClear () => void — back to the normal feed and URL
 */
const FeedSearchHeader = ({ query, total = null, communityName = null, onClear }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{t("dashboard_feed_search_results_for", { query })}</h1>
        <button type="button" className={styles.clear} aria-label={t("dashboard_feed_search_clear_label")} onClick={onClear}>
          <span className={styles.clearLabel}>{t("dashboard_feed_search_clear")}</span>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {Number.isFinite(total) ? (
        <p className={styles.matches}>
          {communityName
            ? t("dashboard_feed_search_matches_in", { count: total, community: communityName })
            : t("dashboard_feed_search_matches_across", { count: total })}
        </p>
      ) : null}
    </div>
  );
};

export default FeedSearchHeader;
